import React, { type FC } from 'react'
import { Box, DialogContent, type SxProps, type Theme, Typography } from '@mui/material'
import { CloudUpload } from '@mui/icons-material'

import { type ConnectwareError, Translation } from '../../../../domain'

import { DocumentationType } from '../../../../application'

import { readFirstFileAsText } from '../..'
import { useAsyncCallback } from '../../Callback'
import { FileUpload, MediumParagraph } from '../../common'
import { DocumentationLink } from '../../Documentation'
import { createFormatter, FormattedList, FormattedTranslation } from '../../Internationalization'
import { useFile, useHasNumberedSteps, useIsEditable, useOnFileChange, useShouldShowUploadInstructions, useSupportedCommissioningFileTypes } from './Hooks'

const uploadFormStyle: SxProps<Theme> = { textAlign: 'center' }
const uploadDialogContentStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'column' }
const uploadDialogEntryStyle: SxProps<Theme> = { display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }
const uploadIconStyle: SxProps<Theme> = { mr: 4, color: 'secondary.main' }

const SimpleFileUploadSection: FC = () => {
    const file = useFile()
    const types = useSupportedCommissioningFileTypes()
    const onFileChange = useOnFileChange()
    const isEditable = useIsEditable()

    const [readFile] = useAsyncCallback(
        async (files: readonly File[]) => {
            const content = await readFirstFileAsText(files).catch((e: ConnectwareError) => e)
            onFileChange(content)
        },
        [onFileChange]
    )

    return (
        <Box data-testid="file-upload-section" sx={uploadDialogEntryStyle}>
            <CloudUpload sx={uploadIconStyle} />
            <FileUpload readOnly={!isEditable} initialValue={file ? [file] : undefined} accept={types} onChange={readFile} />
        </Box>
    )
}

const InstructionsSection: FC = () => {
    const supportedCommissioningFileTypes = useSupportedCommissioningFileTypes()

    const hasSteps = useHasNumberedSteps()
    const shouldShowUploadInstructions = useShouldShowUploadInstructions()

    if (!shouldShowUploadInstructions) {
        return <MediumParagraph id={Translation.COMMISSIONING_FILE} align="left" />
    }

    return (
        <>
            <Typography data-testid="file-upload-summary-1" variant="body1" align="left">
                {hasSteps && '1. '}
                <FormattedTranslation
                    id={Translation.SERVICE_SELECT_COMMISSIONING_FILE}
                    values={{ types: <FormattedList type="disjunction" value={supportedCommissioningFileTypes} /> }}
                />
            </Typography>
            <Typography data-testid="file-upload-summary-2" variant="body2" align="left">
                <FormattedTranslation
                    id={Translation.SERVICE_DOCUMENTATION_LINK}
                    values={{
                        documentationLink: createFormatter(DocumentationLink, { type: DocumentationType.SERVICES }),
                    }}
                />
            </Typography>
        </>
    )
}

export const FileUploadSection: FC = () => (
    <Box sx={uploadFormStyle}>
        <DialogContent sx={uploadDialogContentStyle}>
            <InstructionsSection />
            <SimpleFileUploadSection />
        </DialogContent>
    </Box>
)
